import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface MenuItem {
  icon: string;
  label: string;
  path: string;
  badge?: number;
}

const menuItems: MenuItem[] = [
  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/teacher/dashboard' },
  { icon: 'ri-user-received-line', label: 'Bana Yönlendirilen Öğrenciler', path: '/teacher/ogrencilerim' },
  { icon: 'ri-calendar-check-line', label: 'Deneme Derslerim', path: '/teacher/deneme-derslerim', badge: 1 },
  { icon: 'ri-book-open-line', label: 'Aktif Derslerim', path: '/teacher/aktif-derslerim' },
  { icon: 'ri-sticky-note-line', label: 'Ders Notları', path: '/teacher/ders-notlari' },
  { icon: 'ri-receipt-line', label: 'Dekont Girişleri', path: '/teacher/dekont-girisleri', badge: 2 },
  { icon: 'ri-wallet-3-line', label: 'Kazançlarım', path: '/teacher/kazanclarim' },
  { icon: 'ri-user-settings-line', label: 'Profilim', path: '/teacher/profilim' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TeacherSidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const { logout, profile, ogretmenRecord } = useAuth();

  const displayName = profile?.ad_soyad ?? 'Öğretmen';
  const displayBranches = ogretmenRecord?.branslar?.join(' · ') ?? '';
  const initial = displayName.charAt(0) || 'Ö';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full z-40 w-64 flex flex-col transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'linear-gradient(180deg, #2d1b69 0%, #4a2f8a 60%, #3b2475 100%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 border border-white/15 flex-shrink-0">
            <i className="ri-user-star-line text-white text-base" />
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight tracking-wide">DERSEGEL</p>
            <p className="text-white/40 text-xs">Öğretmen Paneli</p>
          </div>
          <button
            className="ml-auto lg:hidden w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer"
            onClick={onClose}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Teacher profile card */}
        <div className="mx-3 mt-4 mb-2 bg-white/10 border border-white/15 rounded-xl px-3 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#7c6af5] flex-shrink-0">
              <span className="text-white text-sm font-bold">{initial}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{displayName}</p>
              <p className="text-white/40 text-xs truncate">{displayBranches || 'Öğretmen'}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-0.5">
            {menuItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer group ${
                      isActive
                        ? 'bg-white/15 text-white border border-white/10'
                        : 'text-white/60 hover:text-white hover:bg-white/8'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-[#c4b5fd]' : 'text-white/50 group-hover:text-white/80'}`}>
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <span className="flex-1 text-sm">{item.label}</span>
                      {item.badge && (
                        <span className="bg-[#ef4444] text-white text-xs px-1.5 py-0.5 rounded-full font-bold leading-none">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div className="px-3 pb-5 border-t border-white/10 pt-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/50 hover:text-white hover:bg-white/8 w-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line text-base" />
            </div>
            Çıkış Yap
          </button>
        </div>
      </aside>
    </>
  );
}